class Product extends HTMLElement {
  constructor() {
    super();

    this.productForm();
    this.productImages();
    this.productFeatures();
    this.productTabs();
    this.stickyAddToCart();
  }

  productForm() {
    // The gallery follows the variant only once the shopper has actually picked one.
    // Shopify.OptionSelectors fires onVariantSelected during init to sync the UI to the
    // pre-selected variant, and that variant's featured_media is a frame mockup — so on
    // load it scrolled straight off the lifestyle shot main-product.liquid orders first.
    //
    // Keyed on a real click rather than on "init has finished": OptionSelectors only
    // calls back synchronously when the markup carries a [selected] option, and falls
    // back to a setTimeout otherwise (see initDropdown in option-selection.js), so any
    // flag armed after construction would be racing that timer.
    const galleryFollowsVariant = new Set();

    const variants = (productId, sectionId) => {
      const data = JSON.parse(document.getElementById(`ProductJson${productId}`).innerHTML);

      const selectCallback = (variant) => {
        const that = this.querySelector(`.js-product[data-product-id='${productId}']`);

        if (variant) {
          const priceOldElements = that.querySelectorAll(".js-price-old");
          const priceNewElements = that.querySelectorAll(".js-price-new");
          const priceDefaultElements = that.querySelectorAll(".js-price-default");
          if (variant.compare_at_price > variant.price) {
            priceOldElements.forEach(el => {
              el.classList.remove("d-none");
              el.innerHTML = Shopify.formatMoney(variant.compare_at_price, moneyWithCurrencyFormat);
            });
            priceNewElements.forEach(el => {
              el.classList.remove("d-none");
              el.innerHTML = Shopify.formatMoney(variant.price, moneyWithCurrencyFormat);
            });
            priceDefaultElements.forEach(el => {
              el.classList.add("d-none");
            });
          } else {
            priceOldElements.forEach(el => el.classList.add("d-none"));
            priceNewElements.forEach(el => el.classList.add("d-none"));
            priceDefaultElements.forEach(el => {
              el.classList.remove("d-none");
              el.innerHTML = Shopify.formatMoney(variant.price, moneyWithCurrencyFormat);
            });
          }

          const soldOutWrappers = that.querySelectorAll('.js-wrap-sold-out');
          const addToCartWrappers = that.querySelectorAll('.js-wrap-add-to-cart');

          if (!variant.available) {
            soldOutWrappers.forEach(wrapper => wrapper.classList.remove('d-none'));
            addToCartWrappers.forEach(wrapper => wrapper.classList.add('d-none'));
          } else {
            soldOutWrappers.forEach(wrapper => wrapper.classList.add('d-none'));
            addToCartWrappers.forEach(wrapper => wrapper.classList.remove('d-none'));
          }

          if (variant.featured_media) {
            if (galleryFollowsVariant.has(productId)) {
              const carousel = that.querySelector('.js-product-media-carousel');

              // Slides are reordered in Liquid so the lifestyle shot leads, which makes
              // featured_media.position — an admin position — the wrong nth-child. Match
              // on media id and read the display index back off the DOM, falling back to
              // the admin position if a slide carries no id.
              const slides = carousel ? Array.from(carousel.querySelectorAll('.splide__slide')) : [];
              const targetSlide =
                carousel?.querySelector(`.splide__slide[data-media-id="${variant.featured_media.id}"]`) ||
                slides[variant.featured_media.position - 1] ||
                null;
              const targetIndex = targetSlide
                ? slides.indexOf(targetSlide) + 1
                : variant.featured_media.position;

              if (window.innerWidth > 991) {
                if (carousel?.splide) {
                  carousel.splide.go(targetIndex - 1);
                } else if (carousel?.splideInstance) {
                  carousel.splideInstance.go(targetIndex - 1);
                }
              } else if (targetSlide) {
                const carouselRect = carousel.getBoundingClientRect();
                const targetRect = targetSlide.getBoundingClientRect();

                const left = carousel.scrollLeft + (targetRect.left - carouselRect.left);

                carousel.scrollTo({
                  left,
                  behavior: 'smooth'
                });
              }
            }

            that.querySelectorAll('.js-add-to-cart').forEach(button => {
              button.setAttribute('data-featured-image', variant.featured_media.preview_image.src + '&width=300');
            });
          }

          document.querySelectorAll('.js-clearpay-price').forEach(el => {
            el.innerHTML = Shopify.formatMoney(variant.price, window.Shopify.money_format || '£{{amount}}');
          });

          // Additive: nothing in the current layout listens. The stacked layout needs the
          // selected variant in three places at once (its per-size card prices, the price
          // baked into the add to cart label, and scrolling its gallery to the matching
          // frame mockup), and forking this file to get it would duplicate the option
          // selectors, the tabs, the features carousel and the sticky bar alongside it.
          that.dispatchEvent(new CustomEvent('product:variant-change', {
            bubbles: true,
            detail: { variant: variant, productId: productId }
          }));
        } else {
          const soldOutWrappers = that.querySelectorAll('.js-wrap-sold-out');
          const addToCartWrappers = that.querySelectorAll('.js-wrap-add-to-cart');

          soldOutWrappers.forEach(wrapper => wrapper.classList.remove('d-none'));
          addToCartWrappers.forEach(wrapper => wrapper.classList.add('d-none'));
        }
      };

      if (document.getElementById(`product-select-${productId}`)) {
        new Shopify.OptionSelectors(`product-select-${productId}`, {
          product: data,
          onVariantSelected: selectCallback,
          enableHistoryState: true
        });
      }
    };

    document.querySelectorAll(".js-product:not(.active)").forEach((element) => {
      const productId = element.getAttribute("data-product-id");
      const json = document.getElementById(`ProductJson${productId}`);

      if (json) {
        variants(productId);

         element.querySelectorAll('.js-product-size').forEach(sizeElement => {
          sizeElement.addEventListener('click', function (event) {
            event.preventDefault();

            const value = this.getAttribute('data-value');
            const option = this.getAttribute('data-option');
            const product = document.querySelector(`.js-product[data-product-id='${productId}']`);

            const productVariants = product.querySelector(`select.single-option-selector[data-option='${option}']`);

            if (productVariants) {
              galleryFollowsVariant.add(productId);

              productVariants.value = value;
              productVariants.dispatchEvent(new Event('change'));

              const optionValues = product.querySelectorAll(`.js-product-size[data-option="${option}"]`);

              if (optionValues.length) {
                optionValues.forEach(optionValue => {
                  optionValue.classList.remove('active');
                });

                this.classList.add('active');
              }

              const optionWrapper = this.closest('.js-product-option');
              const currentValueElement = optionWrapper?.querySelector('.js-current-option-value');

              if (currentValueElement) {
                let displayValue = value;

                if (value.includes('Small')) displayValue = 'S';
                if (value.includes('Medium')) displayValue = 'M';
                if (value.includes('Large')) displayValue = 'L';

                if (value.includes('(') && value.includes(')')) {
                  displayValue = value.split('(').pop().split(')')[0].trim();
                }

                currentValueElement.textContent = displayValue;
              }
            }
          });
        });

        element.querySelectorAll('.js-product-color').forEach(colorElement => {
          colorElement.addEventListener('click', function (event) {
            event.preventDefault();

            const value = this.getAttribute('data-value');
            const option = this.getAttribute('data-option');
            const product = document.querySelector(`.js-product[data-product-id='${productId}']`);

            const productVariants = product.querySelector(`select.single-option-selector[data-option='${option}']`);

            if (productVariants) {
              galleryFollowsVariant.add(productId);

              productVariants.value = value;
              productVariants.dispatchEvent(new Event('change'));

              const optionValues = product.querySelectorAll(`.js-product-color[data-option="${option}"]`);

              if (optionValues.length) {
                optionValues.forEach(optionValue => {
                  optionValue.classList.remove('active');
                });

                this.classList.add('active');
              }

              const optionWrapper = this.closest('.js-product-option');
              const currentValueElement = optionWrapper?.querySelector('.js-current-option-value');

              if (currentValueElement) {
                currentValueElement.textContent = value;
              }
            }
          });
        });
      }
    });
  }

  productImages() {
    // One source of truth for the boundary the gallery switches on, read by both the mount
    // branch below and the listener at the end of this method, so the two cannot disagree.
    // 992px is `window.innerWidth > 991` expressed as a query, and the same line
    // template-product-v2.scss stops styling the strip at.
    const desktop = window.matchMedia("(min-width: 992px)");

    const initCarousels = () => {
      document.querySelectorAll(".js-product-media-carousel").forEach((el) => {
        if (desktop.matches) {
          if (!el.classList.contains("is-initialized")) {
            // Slide width is per-layout, read off the element. The stacked layout draws a
            // narrower slide than this one in a narrower column, and the two arms otherwise
            // share every line of this mount. Defaulted, so the carousel layout's own markup
            // carries no attributes and gets exactly the widths it always had.
            //
            // Whatever is set here has to stay in step with the track height in CSS, which is
            // 1.2x the slide to match the 120% aspect of a slide's inner box.
            const fixedWidth = el.dataset.fixedWidth || "40.48vw";
            const fixedWidthNarrow = el.dataset.fixedWidthNarrow || "50vw";

            const splide = new Splide(el, {
              type: "slide",
              perPage: 1,
              // The carousel root now spans the full media column (CSS width: 100%).
              // fixedWidth keeps each slide at its previous visible width so the
              // "peek next image" look is preserved, while trimSpace stops the last
              // slide flush against the column's right edge instead of leaving a gap.
              fixedWidth: fixedWidth,
              trimSpace: true,
              // Off, because both layouts hide them anyway: template-product.scss and
              // template-product-v2.scss both carry `.splide__arrows, .splide__pagination {
              // display: none !important; }` — the thumbnail rail is the desktop control in
              // either arm. Mounted on, Splide built two arrow buttons and one pagination
              // button per medium, bound a listener to each, and re-synced the pagination's
              // aria and .is-active on every move, all of it for nodes nothing can see or
              // reach. Turning them off removes that work rather than painting over it.
              //
              // Both CSS rules stay: they are scoped to the product galleries, and every
              // other carousel in the theme mounts its own instance and does want arrows.
              arrows: false,
              pagination: false,
              breakpoints: {
                1298: { fixedWidth: fixedWidthNarrow },
              },
            });

            splide.mount();
            el.splideInstance = splide;
            el.classList.add("is-initialized");
            if (typeof window.enableSplideWheelNav === "function") {
              window.enableSplideWheelNav(splide, el);
            }

            const product = el.closest(".js-product");
            const thumbnails = product?.querySelectorAll(".js-scroll-to-product-media") || [];

            const setActiveThumbnail = (index) => {
              thumbnails.forEach((thumb) => {
                thumb.classList.remove("active");
              });

              const activeThumb = product?.querySelector(
                `.js-scroll-to-product-media[data-index="${index}"]`
              );

              if (activeThumb) {
                activeThumb.classList.add("active");
              }
            };

            setActiveThumbnail(1);

            splide.on("mounted moved", () => {
              setActiveThumbnail(splide.index + 1);
            });
          }
        } else {
          if (el.classList.contains("is-initialized") && el.splideInstance) {
            el.splideInstance.destroy();
            el.splideInstance = null;
            el.classList.remove("is-initialized");
          }
        }
      });
    };

    initCarousels();

    // On the crossing, not on every resize event. Whether this gallery should be mounted
    // changes only when 992px is crossed, but a resize handler re-ran the document-wide
    // querySelectorAll and the whole branch on every pixel of a desktop drag and on every
    // mobile URL-bar collapse or keyboard open — work that could only ever conclude
    // "nothing to do". Splide keeps its own resize handling, so the vw slide widths and the
    // 1298px breakpoint still track the viewport.
    if (typeof desktop.addEventListener === "function") {
      desktop.addEventListener("change", initCarousels);
    } else if (typeof desktop.addListener === "function") {
      // Safari < 14.
      desktop.addListener(initCarousels);
    }

    const lightbox = new PhotoSwipeLightbox({
      gallery: ".js-pswp-gallery",
      children: "a[data-pswp-width][data-pswp-height]",
      pswpModule: PhotoSwipe
    });
    lightbox.init();

    document.querySelectorAll(".js-product").forEach((product) => {
      const productImages = product.querySelector(".js-product-images");
      const thumbnails = product.querySelector(".js-product-thumbnails");
      const gallery = product.querySelector(".js-pswp-gallery");

      const addHover = () => {
        productImages?.classList.add("hover");
      };

      const removeHover = () => {
        productImages?.classList.remove("hover");
      };

      [thumbnails, gallery].forEach((el) => {
        if (!el) return;
        el.addEventListener("mouseenter", addHover);
        el.addEventListener("mouseleave", removeHover);
      });
    });

    document.addEventListener("click", (e) => {
      const trigger = e.target.closest(".js-scroll-to-product-media");
      if (!trigger) return;

      e.preventDefault();
      e.stopPropagation();

      const product = trigger.closest(".js-product");
      if (!product) return;

      const sliderEl = product.querySelector(".js-product-media-carousel");
      if (!sliderEl) return;

      const index = parseInt(trigger.dataset.index, 10) - 1;
      if (isNaN(index) || index < 0) return;

      product.querySelectorAll(".js-scroll-to-product-media").forEach((thumb) => {
        thumb.classList.remove("active");
      });
      trigger.classList.add("active");

      if (window.innerWidth > 991) {
        const splideInstance = sliderEl.splideInstance;
        if (splideInstance) {
          splideInstance.go(index);
        }
      } else {
        const slides = sliderEl.querySelectorAll(".splide__slide, .product__media-item, [data-index]");
        const targetSlide = slides[index];

        if (targetSlide) {
          const left = targetSlide.offsetLeft - sliderEl.offsetLeft;

          sliderEl.scrollTo({
            left,
            behavior: "smooth"
          });
        }
      }
    }, true);
  }

  productFeatures() {
    document.querySelectorAll(".product-features").forEach((features) => {
      if (features.classList.contains("is-initialized")) return;

      const buttons = features.querySelectorAll(".js-change-product-feature");
      const carousel = features.querySelector(".js-product-features-carousel");
      const slides = carousel?.querySelectorAll("li") || [];

      if (!carousel || !buttons.length || !slides.length) return;

      let currentIndex = 0;
      let interval = null;
      let autoplayEnabled = true;

      features.classList.add("is-initialized");

      carousel.style.display = "flex";
      carousel.style.transition = "transform 0.45s ease";
      carousel.style.willChange = "transform";

      slides.forEach((slide) => {
        slide.style.flex = "0 0 100%";
        slide.style.width = "100%";
      });

      const goToSlide = (index) => {
        currentIndex = index;

        carousel.style.transform = `translateX(-${currentIndex * 100}%)`;

        buttons.forEach((button) => {
          button.classList.remove("active");
        });

        const activeButton = features.querySelector(
          `.js-change-product-feature[data-index="${currentIndex + 1}"]`
        );

        if (activeButton) {
          activeButton.classList.add("active");
        }
      };

      const startAutoplay = () => {
        if (!autoplayEnabled || interval || slides.length <= 1) return;

        interval = setInterval(() => {
          const nextIndex = (currentIndex + 1) % slides.length;
          goToSlide(nextIndex);
        }, 2000);
      };

      const stopAutoplay = () => {
        autoplayEnabled = false;

        if (interval) {
          clearInterval(interval);
          interval = null;
        }
      };

      buttons.forEach((button) => {
        button.addEventListener("click", (event) => {
          event.preventDefault();

          const index = parseInt(button.dataset.index, 10) - 1;

          if (isNaN(index) || index < 0 || index >= slides.length) return;

          stopAutoplay();
          goToSlide(index);
        });
      });

      goToSlide(0);
      startAutoplay();
    });
  }

  productTabs() {
    const openButtons = document.querySelectorAll('.js-open-product-tab');
    const tabs = document.querySelectorAll('.js-product-tab');

    if (!openButtons.length || !tabs.length) return;

    let activeTab = null;

    const openTab = (index) => {
      const tab = document.querySelector(`.js-product-tab[data-index="${index}"]`);
      if (!tab) return;

      activeTab = tab;

      tab.classList.add('display');

      if (window.lenis) {
        window.lenis.stop();
      } else {
        document.body.style.overflow = "hidden";
      }

      setTimeout(() => {
        tab.classList.add('active');
      }, 50);
    };

    const closeTab = () => {
      if (!activeTab) return;

      activeTab.classList.add('closing');

      setTimeout(() => {
        activeTab.classList.remove('active', 'closing', 'display');

        if (window.lenis) {
          window.lenis.start();
        } else {
          document.body.style.overflow = "visible";
        }

        activeTab = null;
      }, 300);
    };

    openButtons.forEach(button => {
      button.addEventListener('click', (event) => {
        event.preventDefault();

        const index = button.getAttribute('data-index');
        if (!index) return;

        openTab(index);
      });

      // Non-native controls (e.g. the product tag) get role="button" — add
      // Enter/Space activation since they don't fire click on key press.
      if (button.getAttribute('role') === 'button') {
        button.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            button.click();
          }
        });
      }
    });

    document.querySelectorAll('.js-close-product-tab').forEach(button => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        closeTab();
      });
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        closeTab();
      }
    });
  }

  // The mobile add to cart sits in the page and pins to the bottom of the screen only
  // once it has been scrolled past, unpinning again over the footer it would cover.
  //
  // The existing bar is promoted in place rather than a second one being rendered. A
  // duplicate would be picked up by the delegated .js-add-to-cart handler in
  // drawer-cart.js and by every querySelectorAll in productForm above, so the two
  // copies would fight over the sold-out swap and double-count the add to cart.
  stickyAddToCart() {
    const anchor = this.querySelector('.js-atc-anchor');

    if (!anchor || !('IntersectionObserver' in window)) {
      return;
    }

    // Two layouts, one implementation: the carousel layout's bar is .product__add-to-cart,
    // the stacked layout's is .product-v2__atc. Both are wrapped in .js-atc-anchor and pin
    // the same way, so the only thing that differs is which class to reach for.
    const bar = anchor.querySelector('.product__add-to-cart, .product-v2__atc');

    if (!bar) {
      return;
    }

    // Matches the breakpoint the CSS pins at. Without re-checking on change, rotating
    // or resizing up to desktop would strand a pinned bar over the page.
    const mobile = window.matchMedia('(max-width: 991px)');
    const footer = document.querySelector('.footer');

    let scrolledPast = false;
    let footerVisible = false;

    // Only meaningful while the bar is in flow: pinned it carries 16px of padding the
    // flow version does not, so measuring it then would leave the placeholder — and so
    // everything below it — taller than the gap the bar actually vacated.
    const measure = () => {
      if (anchor.classList.contains('is-pinned')) {
        return;
      }

      anchor.style.setProperty('--atc-height', `${bar.offsetHeight}px`);
    };

    const apply = () => {
      const pinned = mobile.matches && scrolledPast && !footerVisible;

      if (pinned) {
        measure();
      }

      anchor.classList.toggle('is-pinned', pinned);
    };

    const anchorObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        // Above the viewport, not merely outside it. The anchor starts below the fold,
        // and testing isIntersecting alone would pin the bar there on page load — the
        // always-visible bar this setting exists to replace.
        scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        apply();
      });
    });

    anchorObserver.observe(anchor);

    if (footer) {
      // No rootMargin: the footer's top edge crossing the bottom of the viewport is the
      // same moment the bar starts covering it.
      const footerObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          footerVisible = entry.isIntersecting;
          apply();
        });
      });

      footerObserver.observe(footer);
    }

    if ('ResizeObserver' in window) {
      // The bar's height moves with the sold-out swap and the next-day production
      // message, so the placeholder is measured rather than hard-coded. Fires once on
      // observe, which is also what seeds --atc-height.
      new ResizeObserver(measure).observe(bar);
    } else {
      measure();
    }

    if (typeof mobile.addEventListener === 'function') {
      mobile.addEventListener('change', apply);
    } else if (typeof mobile.addListener === 'function') {
      mobile.addListener(apply);
    }
  }
}

if(document.querySelector("product-page")) {
  customElements.define("product-page", Product);
}